import { FORMATION_GAME_SIZE, type FormationGameSize, type TrainingFormationDto } from '@ukinfo/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formationMeta, gameSizeLabel } from '../../lib/formations';
import { Badge, cn } from '../ui';
import { FormationPreview } from './FormationPitch';

/**
 * Bausteine der Formationenliste - gemeinsam fuer die Seite "Formationen" und die Auswahl im Skizzeneditor.
 */

export type GameSizeFilter = FormationGameSize | 'ALL';

/** Die Liste zur Spielform: Standard und eigene gemischt, wie der Server sie liefert. */
export function useFormationList(gameSize: GameSizeFilter, enabled = true) {
  return useQuery({
    queryKey: ['training-formations', { gameSize }],
    queryFn: () => api.get<TrainingFormationDto[]>('/api/training/formations', { gameSize: gameSize === 'ALL' ? undefined : gameSize }),
    enabled,
    // Beim Umschalten bleibt die bisherige Liste stehen, bis die neue da ist.
    placeholderData: keepPreviousData,
  });
}

/** Auswahl der Spielform als Knopfreihe: alle, 11er, 9er, 7er, 5er. */
export function GameSizeFilterBar({ value, onChange, className }: { value: GameSizeFilter; onChange: (value: GameSizeFilter) => void; className?: string }): JSX.Element {
  const options: { value: GameSizeFilter; label: string }[] = [
    { value: 'ALL', label: 'Alle' },
    ...FORMATION_GAME_SIZE.map((size) => ({ value: size, label: gameSizeLabel(size) })),
  ];
  return (
    <div role="group" aria-label="Spielform" className={cn('flex flex-wrap gap-1.5', className)}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'min-h-11 min-w-14 rounded-xl border px-3 py-1.5 text-sm font-medium shadow-sm transition-colors sm:min-h-0',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60',
            value === option.value ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Kennzeichen einer Formation: Standard oder eigene (keines). */
export function FormationBadges({ formation }: { formation: Pick<TrainingFormationDto, 'source'> }): JSX.Element {
  return formation.source === 'CATALOG' ? <Badge tone="brand" title="Aus den Standardformationen – nicht änderbar, aber kopierbar">Standard</Badge> : <></>;
}

/** Kleine Vorschau mit Name und Merkmalen - der Inhalt einer Karte oder einer Auswahlzeile. */
export function FormationSummary({ formation }: { formation: TrainingFormationDto }): JSX.Element {
  return (
    <>
      <div className="w-20 shrink-0 sm:w-24">
        <FormationPreview positions={formation.positions} className="max-h-32 rounded-lg" />
      </div>
      <span className="min-w-0 flex-1">
        <span className="block break-words font-semibold text-slate-900">{formation.name}</span>
        <span className="mt-1 flex flex-wrap gap-1">
          <FormationBadges formation={formation} />
        </span>
        <span className="mt-1 block text-xs text-slate-500">{formationMeta(formation)}</span>
        {formation.description && <span className="mt-0.5 line-clamp-2 break-words text-xs text-slate-500">{formation.description}</span>}
      </span>
    </>
  );
}
